import { createAsyncThunk } from '@reduxjs/toolkit';
import { apiGet, apiPost, apiRemove } from 'api-service';
import { removeSelectedVipResource } from 'store';
import {
  adaptVipResource,
  adaptVipResources,
  normalizeVipList,
} from './vipResources.adapter';

const VIP_SEARCH_RESOURCE_TYPE = {
  instagram: 'instagram',
  twitter: 'twitter',
  telegram: 'telegram',
};

const toVipSearchType = (platform) =>
  VIP_SEARCH_RESOURCE_TYPE[platform] || String(platform || '').toLowerCase();

export const fetchVipResources = createAsyncThunk(
  'vipResourcesReducer/fetchVipResources',
  async ({ platform, ...otherParams }, thunkAPI) => {
    try {
      const response = await apiGet('/search/vip', {
        resource_type: toVipSearchType(platform),
        no_paginate: 1,
        ...otherParams,
      });
      return {
        vipResources: adaptVipResources(
          normalizeVipList(response.data?.result ?? response.data)
        ),
      };
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const fetchPlatformsVipResources = createAsyncThunk(
  'vipResourcesReducer/fetchPlatformsVipResources',
  async (_, thunkAPI) => {
    const { instagram, twitter, telegram } =
      thunkAPI.getState().accountReducer.currentUser.user.permissions
        .SearchPlatforms;
    try {
      await Promise.all([
        instagram.permission &&
          thunkAPI.dispatch(fetchVipResources({ platform: 'instagram' })),
        twitter.permission &&
          thunkAPI.dispatch(fetchVipResources({ platform: 'twitter' })),
        telegram.permission &&
          thunkAPI.dispatch(fetchVipResources({ platform: 'telegram' })),
      ]);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const removeVipResource = createAsyncThunk(
  'vipResourcesReducer/removeVipResource',
  async ({ vipResourceId, platform }, thunkAPI) => {
    if (!vipResourceId) {
      return thunkAPI.rejectWithValue(new Error('missing-vip-id'));
    }

    /**
     * Callers may pass the VIP record id (ResourceVipManage) or the resource
     * id (profile / onboarding). Resolve to the VIP row id before DELETE.
     */
    const vipResource = thunkAPI
      .getState()
      .vipResourcesReducer.vipResources[platform]?.data?.find(
        (item) =>
          item.vipId === vipResourceId ||
          item.id === vipResourceId ||
          item.resource_id === vipResourceId
      );
    const deleteId = vipResource?.vipId ?? vipResource?.id ?? vipResourceId;

    try {
      const response = await apiRemove(`/search/vip/${deleteId}`);
      thunkAPI.dispatch(
        removeSelectedVipResource({ vipResourceId: deleteId, platform })
      );
      return response?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const addVipResource = createAsyncThunk(
  'vipResourcesReducer/addVipResource',
  async ({ platform, ...otherParams }, thunkAPI) => {
    try {
      const username = otherParams.username || otherParams.data?.[0]?.username;
      const payload = {
        resource_type: toVipSearchType(platform),
      };
      if (otherParams.resource_id)
        payload.resource_id = otherParams.resource_id;
      if (username) payload.username = username;

      const response = await apiPost('/search/vip', payload);
      const created = response.data.result?.data ?? response.data?.data;
      const resource = Array.isArray(created) ? created[0] : created;
      return {
        vipResource: resource ? adaptVipResource(resource) : resource,
      };
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const fetchSocialAccountInfoByUsername = createAsyncThunk(
  'vipResourcesReducer/fetchSocialAccountInfoByUsername',
  async ({ username, platform }, thunkAPI) => {
    try {
      let endpoint = '';

      switch (platform) {
        case 'telegram':
          endpoint = `/vip/telegram/api/v2/${username}`;
          break;
        case 'instagram':
          endpoint = `/vip/instagram/instagram/v1/user/${username}`;
          break;
        case 'twitter':
          endpoint = `/vip/dataakcraft/twitter/v1/non_login_user/${username}`;
          break;
        default:
          throw new Error(`Unknown platform: ${platform}`);
      }

      const response = await apiGet(endpoint);

      switch (platform) {
        case 'telegram':
          return response?.data?.data?.raw_json;
        case 'instagram':
          return response?.data?.data?.userInfo;
        case 'twitter':
          return response?.data?.data?.userInfo;
        default:
          return null;
      }
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const fetchVipPlatformInfo = createAsyncThunk(
  'vipResourcesReducer/fetchVipPlatformInfo',
  async (_, thunkAPI) => {
    try {
      const response = await apiGet('/search/vip/limit-info');

      return response.data.result.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);
